/*
 * =============================================================================
 * FILE HEADER — packages/ir/src/schemas/candidate.ts
 * =============================================================================
 *
 * CHANGE LOG
 * ----------
 * | # | Date & Time (IST)      | Who               | Why             | What Changed                    |
 * |---|------------------------|-------------------|-----------------|---------------------------------|
 * | 1 | 2026-06-16 (Phase 1)   | Claude Sonnet 4.6 | IR Step 1       | Created — Candidate, Selector,  |
 * |   |                        |                   |                 | Fingerprint zod schemas         |
 * | 2 | 2026-07-06             | Claude Sonnet 5   | AI Assist port  | Added "assist" to addedBy enum  |
 * |   |                        |                   | (from Plex)     | so Assist-authored candidates   |
 * |   |                        |                   |                 | are audit-tagged distinctly     |
 *
 * WHICH FILE CALLS / IMPORTS THIS FILE
 * --------------------------------------
 * - packages/ir/src/schemas/steps.ts   (SelectorSchema on each step with a selector)
 * - packages/ir/src/index.ts           (re-exported as public API)
 *
 * FUNCTIONS / EXPORTS COVERED
 * ----------------------------
 * CandidateStrategySchema — z.enum of all valid strategy strings
 * FingerprintSchema       — boundsRatio 4-tuple, neighbors, semanticText, refs
 * CandidateSchema         — strategy + value + addedBy + index? + within?
 * SelectorSchema          — description + candidates[] + fingerprint?
 * CandidateStrategy       — TypeScript union type (inferred from schema)
 * Fingerprint             — TypeScript type (inferred)
 * Candidate               — TypeScript type (inferred)
 * Selector                — TypeScript type (inferred)
 *
 * CALLER → CALLEE MAP
 * --------------------
 * steps.ts  → SelectorSchema
 * index.ts  → all exports
 *
 * IMPORTANT POINTS
 * -----------------
 * 1. Selector and Candidate are mutually recursive (Candidate.within is a Selector).
 *    Both use z.lazy() to break the cycle. TypeScript types are declared explicitly
 *    to avoid inference-depth issues.
 * 2. candidates[] is deliberately allowed to be empty — the recorder can produce a
 *    selector with no reliable candidates, relying only on fallbackCoord. Validation
 *    at higher levels (validateIR) may warn but must not reject empty-candidate steps.
 * 3. Fingerprint.boundsRatio is a 4-tuple [x, y, w, h] normalized 0..1. The tuple
 *    type uses z.tuple to enforce exactly 4 numbers.
 * 4. Selectors are deeply immutable in the runner — never mutate candidates in place;
 *    always replace with a new array (healing prepends a new candidate).
 *
 * ISSUES FACED
 * ------------
 * - z.lazy + recursive types require explicit TypeScript type annotations; Zod cannot
 *   infer through the cycle automatically.
 *
 * =============================================================================
 */
import { z } from "zod";
// ── Candidate strategy ───────────────────────────────────────────────────────
export const CandidateStrategySchema = z.enum([
    "a11yId", // Android resource-id / iOS accessibilityIdentifier / data-testid
    "text", // Exact visible text match
    "textContains", // Substring text match
    "textRegex", // Regex text match (value is a JS regex string)
    "contentDesc", // content-desc / aria-label / accessibilityLabel
    "className", // Android class / iOS XCUIElementType — low confidence, emitted only by importers
    "iosPredicate", // XCUITest native predicate string (iOS only)
    "iosClassChain", // XCUITest class chain (iOS only)
    "css", // CSS selector (web only)
    "xpath", // XPath (all platforms, last resort)
    "relative", // PEG-parsed: below(a11yId='x') and className('...')
]);
// ── Fingerprint ──────────────────────────────────────────────────────────────
const NeighborSchema = z.object({
    above: z.string().optional(),
    below: z.string().optional(),
    left: z.string().optional(),
    right: z.string().optional(),
});
export const FingerprintSchema = z.object({
    /** Concatenated id|text|contentDesc|className for embedding similarity. */
    semanticText: z.string(),
    /** Artifact id of precomputed f32 embedding vector. Populated lazily by healer. */
    embeddingRef: z.string().optional(),
    /** Artifact id of element screenshot crop captured at record time. */
    cropRef: z.string().optional(),
    /** [x, y, w, h] normalized 0..1 relative to screen dimensions at record time. */
    boundsRatio: z.tuple([z.number(), z.number(), z.number(), z.number()]),
    /** Text of the nearest neighbor elements; used for neighbor-overlap scoring. */
    neighbors: NeighborSchema,
    /** Hash of sorted visible texts on screen at record time; same-screen gate for Tier-1. */
    screenSignature: z.string(),
});
// Zod schemas using z.lazy() to handle the mutual recursion.
export const CandidateSchema = z.lazy(() => z.object({
    strategy: CandidateStrategySchema,
    value: z.string(),
    addedBy: z.enum(["recorder", "user", "heal", "assist"]),
    /** 0-indexed. Picks the Nth match when multiple nodes satisfy the selector. Default 0. */
    index: z.number().int().min(0).optional(),
    /** Scope this candidate to a sub-tree rooted at another selector. */
    within: SelectorSchema.optional(),
}));
export const SelectorSchema = z.lazy(() => z.object({
    /** Human-readable label. Acts as semantic anchor for Tier-1 healing. */
    description: z.string(),
    /** Tried strictly in order (Tier-0). Empty = no semantic candidates; rely on fallbackCoord. */
    candidates: z.array(CandidateSchema),
    fingerprint: FingerprintSchema.optional(),
}));
//# sourceMappingURL=candidate.js.map